import { useEffect, useState } from 'react';
import axios from 'axios';
import { WinRadio, WinCheckbox } from './WinComponents';
import DateInput from './DateInput';
import { formatNomPropre, formatNAS, formatCodePostal, formatTelephone, calculerAge } from '../utils/formatters';
import VilleAutocomplete from './VilleAutocomplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LiensTab = ({
  formData, provinces, showGestionLiens, setShowGestionLiens, loadLiensFromServer,
  liensListe, isLoadingLiens, lienEnEdition, setLienEnEdition,
  handleNouveauLien, handleEditerLien, handleSauvegarderLien, handleSupprimerLienServeur,
  handleDefinirCommeLienExclusif, showPopup
}) => {
  const emptyLienForm = { id: null, type_lien: '', nom: '', prenom: '', date_naissance: '', sexe: '1', nas: '', interets_opposes: false, adresse: '', ville: '', province: 'QC', code_postal: '', telephone_residence: '', telephone_bureau: '', telephone_ext: '', courriel: '' };

  // === Types de liens — chargés DYNAMIQUEMENT depuis /api/reference/liens ===
  // Parité stricte avec la table legacy `Static.dbo.liens` (VB.NET FrmLiens.vb :
  // `Me.cboLiens.DataSource = listeLiens` où `listeLiens = SELECT * FROM liens
  // ORDER BY description`). Ne JAMAIS hardcoder cette liste — le contenu de la
  // table peut évoluer côté administration de l'aide juridique.
  const [typesLiens, setTypesLiens] = useState([{ value: '', label: '' }]);
  const [typesLiensError, setTypesLiensError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/reference/liens`);
        if (cancelled) return;
        const opts = [{ value: '', label: '' }].concat(
          (data || []).map(r => ({ value: r.description, label: r.description }))
        );
        setTypesLiens(opts);
        setTypesLiensError('');
      } catch (e) {
        if (cancelled) return;
        setTypesLiensError(e?.response?.data?.detail || e.message || 'Erreur');
        setTypesLiens([{ value: '', label: '' }]);
      }
    })();
    return () => { cancelled = true; };
  }, []);


  const renderLienReadOnly = (lienNum, lienData) => (
    <fieldset className="win-groupbox compact" style={{margin: 0}}>
      <legend style={{display: 'flex', alignItems: 'center', gap: 16}}>
        <span>Lien {lienNum}</span>
      </legend>
      {lienData.nom ? (
        <>
          {/* Type de lien — champ dédié visible en tête (parité VB.NET FrmDaj.vb) */}
          <div className="win-field" style={{marginBottom: 8}}>
            <label className="win-field-label">Type de lien</label>
            <input
              className="win-input"
              value={(lienData.type_lien || '').trim() || '-'}
              readOnly
              tabIndex={-1}
              data-testid={`lien${lienNum}-type-lien-readonly`}
              style={{fontWeight: 'bold', background: '#f0f8ff'}}
            />
          </div>
          <div className="win-compact-grid-2">
            <div className="win-field">
              <label className="win-field-label">Nom</label>
              <input className="win-input" value={lienData.nom} readOnly tabIndex={-1} />
            </div>
            <div className="win-field">
              <label className="win-field-label">Prénom</label>
              <input className="win-input" value={lienData.prenom || ''} readOnly tabIndex={-1} />
            </div>
          </div>
          <div className="win-compact-grid-2" style={{marginTop: 8}}>
            <div className="win-field">
              <label className="win-field-label">Date naissance</label>
              <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <input className="win-input" value={lienData.date_naissance || '-'} readOnly tabIndex={-1} />
                {calculerAge(lienData.date_naissance) !== null && <span style={{fontSize: 12, color: '#555', whiteSpace: 'nowrap', fontWeight: 'bold'}}>{calculerAge(lienData.date_naissance)} ans</span>}
              </div>
            </div>
            <div className="win-field">
              <label className="win-field-label">Sexe</label>
              <input className="win-input" value={lienData.sexe === '1' || lienData.sexe === 1 ? 'M' : 'F'} readOnly tabIndex={-1} style={{width: 40}} />
            </div>
          </div>
          <div className="win-compact-grid-2" style={{marginTop: 8}}>
            <div className="win-field">
              <label className="win-field-label">N.A.S.</label>
              <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <input className="win-input" value={lienData.nas || '-'} readOnly tabIndex={-1} style={{width: 120}} />
                {lienData.interets_opposes && <span style={{color: '#dc2626', fontWeight: 'bold', fontSize: 13}}>Intérêts opposés</span>}
              </div>
            </div>
          </div>
          <div style={{marginTop: 8}}>
            <div style={{display: 'grid', gridTemplateColumns: '2fr 1fr 80px 50px', gap: 12}}>
              <div className="win-field">
                <label className="win-field-label">Adresse</label>
                <input className="win-input" value={lienData.adresse || '-'} readOnly tabIndex={-1} />
              </div>
              <div className="win-field">
                <label className="win-field-label">Ville</label>
                <input className="win-input" value={lienData.ville || '-'} readOnly tabIndex={-1} />
              </div>
              <div className="win-field">
                <label className="win-field-label">Code postal</label>
                <input className="win-input" value={lienData.code_postal || '-'} readOnly tabIndex={-1} />
              </div>
              <div className="win-field">
                <label className="win-field-label">Prov.</label>
                <input className="win-input" value={lienData.province || '-'} readOnly tabIndex={-1} />
              </div>
            </div>
          </div>
          <div className="win-compact-grid-2" style={{marginTop: 8}}>
            <div className="win-field">
              <label className="win-field-label">Tél. résidence</label>
              <input className="win-input" value={lienData.telephone_residence || '-'} readOnly tabIndex={-1} />
            </div>
            <div className="win-field">
              <label className="win-field-label">Tél. bureau</label>
              <input className="win-input" value={lienData.telephone_bureau || '-'} readOnly tabIndex={-1} />
            </div>
          </div>
        </>
      ) : (
        <div style={{padding: '30px 0', textAlign: 'center', color: '#94a3b8', fontSize: 14, fontStyle: 'italic'}}>Aucun lien défini</div>
      )}
    </fieldset>
  );

  return (
    <>
      <div className="win-tab-content compact">
        <div style={{display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16}}>
          <button className="win-btn-action" onClick={() => { loadLiensFromServer(); setShowGestionLiens(true); }} data-testid="btn-gerer-liens" disabled={!formData.noclient} style={{fontWeight: 'bold', whiteSpace: 'nowrap'}}>
            GESTION DES LIENS
          </button>
          <div className="win-info-box" style={{margin: 0, flex: 1}}>
            <strong>Note:</strong> Le conjoint est géré dans l'onglet "Conjoint". Cliquez sur "GESTION DES LIENS" pour ajouter ou modifier les liens familiaux associés à ce dossier.
          </div>
        </div>
        <div className="win-liens-grid">
          {renderLienReadOnly(1, formData.lien1)}
          {renderLienReadOnly(2, formData.lien2)}
        </div>
      </div>

      {/* Modal Gestion des Liens */}
      {showGestionLiens && (
        <div className="popup-overlay">
          <div className="gestion-liens-modal" onClick={e => e.stopPropagation()}>
            <div className="popup-header" style={{ background: '#0033A0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <span className="popup-title">GESTION DES LIENS FAMILIAUX</span>
              </div>
              <button className="win-modal-close" onClick={() => { setShowGestionLiens(false); setLienEnEdition(null); }}>{'×'}</button>
            </div>
            
            <div className="gestion-liens-content" style={{ paddingTop: 10 }}>
              {/* Formulaire */}
              <div className="lien-edition-section">
                  <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12}}>
                    <label className="win-field-label" style={{margin: 0, whiteSpace: 'nowrap'}}>Type de lien *</label>
                    {(() => {
                      // Legacy tolerance : le `<select>` React n'affiche RIEN si
                      // la valeur courante ne matche aucune <option>. Or les
                      // données legacy peuvent contenir des types de liens qui
                      // ne sont plus dans `Static.liens` (obsolètes, casse ≠,
                      // accents ≠, padding CHAR(N)). On tolère donc le trim +
                      // case-insensitive avant de flaguer « (legacy) ». On
                      // attend AUSSI que typesLiens soit réellement chargé
                      // (>1 entrée, la 1ère étant l'option vide) pour éviter
                      // le faux positif au premier render pendant le fetch async.
                      const currentValue = (lienEnEdition || emptyLienForm).type_lien || '';
                      const currentNormalized = currentValue.trim().toLowerCase();
                      const typesLoaded = typesLiens.length > 1;
                      // Trouve un match tolérant (trim + case-insensitive)
                      const matchedOpt = typesLiens.find(t => (t.value || '').trim().toLowerCase() === currentNormalized);
                      // Si le currentValue matche par tolérance mais avec une casse/espaces différents,
                      // on remappe automatiquement sur la valeur canonique (évite bug de sélection).
                      const canonicalValue = matchedOpt ? matchedOpt.value : currentValue;
                      const isLegacy = typesLoaded && currentValue && !matchedOpt;
                      return (
                        <select
                          className="win-select"
                          value={canonicalValue}
                          onChange={e => setLienEnEdition({...(lienEnEdition || emptyLienForm), type_lien: e.target.value})}
                          style={{minWidth: 200}}
                          data-testid="select-type-lien"
                        >
                          <option value="">-- Sélectionner --</option>
                          {isLegacy && (
                            <option key="__legacy__" value={currentValue} style={{fontStyle: 'italic', color: '#8b6914'}}>
                              {`${currentValue} (legacy)`}
                            </option>
                          )}
                          {typesLiens.filter(t => t.value !== '').map(t => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </select>
                      );
                    })()}
                    {typesLiensError && (
                      <span style={{fontSize: 11, color: '#c00'}} data-testid="types-liens-error">
                        Erreur chargement types de liens : {typesLiensError}
                      </span>
                    )}
                    {lienEnEdition?.id && <span style={{fontSize: 11, color: '#666', fontStyle: 'italic'}}>Modification en cours</span>}
                    {/* Boutons déplacés à droite sur la ligne Type de lien */}
                    <div style={{display: 'flex', gap: 8, marginLeft: 'auto'}}>
                      {lienEnEdition?.id !== undefined && lienEnEdition?.id !== null && (
                        <button className="win-btn" onClick={() => setLienEnEdition({...emptyLienForm})} data-testid="btn-nouveau-lien" style={{padding: '6px 16px', fontSize: '12px'}}>Nouveau</button>
                      )}
                      <button className="win-btn" onClick={handleSauvegarderLien} data-testid="btn-sauvegarder-lien" style={{padding: '6px 16px', fontSize: '12px'}}>Sauvegarder</button>
                    </div>
                  </div>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16}}>
                    <fieldset className="win-groupbox compact" style={{margin: 0, padding: 10}}>
                      <legend style={{fontSize: 11, padding: '3px 10px'}}>IDENTIFICATION</legend>
                      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8}}>
                        <div className="win-field">
                          <label className="win-field-label" style={{fontSize: 10}}>Nom *</label>
                          <input className="win-input" style={{fontSize: 14, padding: '4px 8px'}} value={(lienEnEdition || emptyLienForm).nom} onChange={e => setLienEnEdition({...(lienEnEdition || emptyLienForm), nom: formatNomPropre(e.target.value)})} maxLength={40} />
                        </div>
                        <div className="win-field">
                          <label className="win-field-label" style={{fontSize: 10}}>Prénom *</label>
                          <input className="win-input" style={{fontSize: 14, padding: '4px 8px'}} value={(lienEnEdition || emptyLienForm).prenom} onChange={e => setLienEnEdition({...(lienEnEdition || emptyLienForm), prenom: formatNomPropre(e.target.value)})} maxLength={40} />
                        </div>
                      </div>
                      <div style={{display: 'grid', gridTemplateColumns: '180px 1fr', gap: 8, marginTop: 8}}>
                        <div className="win-field">
                          <label className="win-field-label" style={{fontSize: 10}}>Date naissance</label>
                          <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                            <DateInput value={(lienEnEdition || emptyLienForm).date_naissance} onChange={v => setLienEnEdition({...(lienEnEdition || emptyLienForm), date_naissance: v})} testId="input-datenaissance-lien" />
                            {calculerAge((lienEnEdition || emptyLienForm).date_naissance) !== null && <span style={{fontSize: 11, color: '#555', whiteSpace: 'nowrap', fontWeight: 'bold'}}>{calculerAge((lienEnEdition || emptyLienForm).date_naissance)} ans</span>}
                          </div>
                        </div>
                        <div className="win-field">
                          <label className="win-field-label" style={{fontSize: 10}}>Sexe</label>
                          <div className="win-radio-group">
                            <WinRadio name="sexe_lien_edit" value="0" checked={(lienEnEdition || emptyLienForm).sexe === '0'} onChange={v => setLienEnEdition({...(lienEnEdition || emptyLienForm), sexe: v})} label="F" />
                            <WinRadio name="sexe_lien_edit" value="1" checked={(lienEnEdition || emptyLienForm).sexe === '1'} onChange={v => setLienEnEdition({...(lienEnEdition || emptyLienForm), sexe: v})} label="M" />
                          </div>
                        </div>
                      </div>
                      <div style={{display: 'flex', alignItems: 'flex-end', gap: 16, marginTop: 8}}>
                        <div className="win-field">
                          <label className="win-field-label" style={{fontSize: 10}}>N.A.S.</label>
                          <input className="win-input" style={{fontSize: 14, padding: '4px 8px', width: 120}} value={(lienEnEdition || emptyLienForm).nas} onChange={e => setLienEnEdition({...(lienEnEdition || emptyLienForm), nas: formatNAS(e.target.value)})} placeholder="000-000-000" maxLength={11} />
                        </div>
                        <WinCheckbox label="Intérêts opposés" checked={(lienEnEdition || emptyLienForm).interets_opposes} onChange={v => setLienEnEdition({...(lienEnEdition || emptyLienForm), interets_opposes: v})} />
                      </div>
                    </fieldset>
                    <fieldset className="win-groupbox compact" style={{margin: 0, padding: 10}}>
                      <legend style={{fontSize: 11, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 8}}>
                        COORDONNÉES
                        <button
                          className="win-btn"
                          type="button"
                          title="Copier l'adresse du requérant"
                          data-testid="btn-copier-adresse-lien"
                          style={{padding: '2px 5px', lineHeight: 1, minWidth: 'unset'}}
                          onClick={() => setLienEnEdition(prev => ({
                            ...(prev || emptyLienForm),
                            adresse: formData.adresse || '',
                            ville: formData.ville || '',
                            province: formData.province || 'QC',
                            code_postal: formData.code_postal || '',
                            telephone_residence: formData.telephone_residence || '',
                            telephone_bureau: formData.telephone_bureau || '',
                            telephone_ext: formData.telephone_ext || '',
                          }))}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/></svg>
                        </button>
                      </legend>
                      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8}}>
                        <div className="win-field">
                          <label className="win-field-label" style={{fontSize: 10}}>Adresse</label>
                          <input className="win-input" style={{fontSize: 14, padding: '4px 8px'}} value={(lienEnEdition || emptyLienForm).adresse} onChange={e => setLienEnEdition({...(lienEnEdition || emptyLienForm), adresse: e.target.value})} maxLength={40} />
                        </div>
                        <div className="win-field">
                          <label className="win-field-label" style={{fontSize: 10}}>Ville</label>
                          <VilleAutocomplete style={{fontSize: 14, padding: '4px 8px'}} value={(lienEnEdition || emptyLienForm).ville} onChange={v => setLienEnEdition({...(lienEnEdition || emptyLienForm), ville: formatNomPropre(v)})} maxLength={40} testId="lien-ville" />
                        </div>
                      </div>
                      <div style={{display: 'grid', gridTemplateColumns: '80px 55px 1fr 1fr', gap: 8, marginTop: 8}}>
                        <div className="win-field">
                          <label className="win-field-label" style={{fontSize: 10}}>Code postal</label>
                          <input className="win-input" style={{fontSize: 14, padding: '4px 8px'}} value={(lienEnEdition || emptyLienForm).code_postal} onChange={e => setLienEnEdition({...(lienEnEdition || emptyLienForm), code_postal: formatCodePostal(e.target.value)})} maxLength={7} placeholder="A1A 1A1" />
                        </div>
                        <div className="win-field">
                          <label className="win-field-label" style={{fontSize: 10}}>Prov.</label>
                          <select className="win-select" style={{fontSize: 14, padding: '4px 6px', minWidth: 'unset'}} value={(lienEnEdition || emptyLienForm).province} onChange={e => setLienEnEdition({...(lienEnEdition || emptyLienForm), province: e.target.value})}>
                            {provinces.map(p => <option key={p.value} value={p.value}>{p.value}</option>)}
                          </select>
                        </div>
                        <div className="win-field">
                          <label className="win-field-label" style={{fontSize: 10}}>Tél. résidence</label>
                          <input className="win-input" style={{fontSize: 14, padding: '4px 8px'}} value={(lienEnEdition || emptyLienForm).telephone_residence} onChange={e => setLienEnEdition({...(lienEnEdition || emptyLienForm), telephone_residence: formatTelephone(e.target.value)})} maxLength={12} />
                        </div>
                        <div className="win-field">
                          <label className="win-field-label" style={{fontSize: 10}}>Tél. bureau</label>
                          <input className="win-input" style={{fontSize: 14, padding: '4px 8px'}} value={(lienEnEdition || emptyLienForm).telephone_bureau} onChange={e => setLienEnEdition({...(lienEnEdition || emptyLienForm), telephone_bureau: formatTelephone(e.target.value)})} maxLength={12} />
                        </div>
                      </div>
                      <div style={{marginTop: 8}}>
                        <div className="win-field">
                          <label className="win-field-label" style={{fontSize: 10}}>Courriel</label>
                          <input className="win-input" type="email" style={{fontSize: 14, padding: '4px 8px'}} value={(lienEnEdition || emptyLienForm).courriel} onChange={e => setLienEnEdition({...(lienEnEdition || emptyLienForm), courriel: e.target.value.toLowerCase()})} maxLength={100} />
                        </div>
                      </div>
                    </fieldset>
                  </div>
              </div>

              {/* Liste en bas */}
              <div className="liens-liste-section" style={{marginTop: 16}}>
                {isLoadingLiens ? (
                  <p>Chargement...</p>
                ) : liensListe.length === 0 ? (
                  <div className="liens-table-container" style={{display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'}}>
                    <p className="liens-vide">Aucun lien enregistré pour ce client.</p>
                  </div>
                ) : (
                  <div className="liens-table-container" style={{maxHeight: '200px', overflowY: 'auto'}}>
                    <table className="win-table liens-table" style={{width: '100%'}}>
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Nom</th>
                          <th>Prénom</th>
                          <th>Date naiss.</th>
                          <th style={{width: '100px', textAlign: 'center'}}>Lien</th>
                          <th style={{width: '50px', textAlign: 'center'}}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {liensListe.map((lien, idx) => {
                          const isLien1 = formData.lien1.nom === lien.nom && formData.lien1.prenom === lien.prenom && formData.lien1.date_naissance === lien.date_naissance;
                          const isLien2 = formData.lien2.nom === lien.nom && formData.lien2.prenom === lien.prenom && formData.lien2.date_naissance === lien.date_naissance;
                          const isSelected = lienEnEdition && lienEnEdition.id === lien.id;
                          return (
                            <tr key={lien.id || idx} onClick={() => handleEditerLien(lien)} style={{cursor: 'pointer', background: isSelected ? '#e0edff' : undefined}}>
                              <td>{lien.type_lien}</td>
                              <td>{lien.nom}</td>
                              <td>{lien.prenom}</td>
                              <td>{lien.date_naissance || '-'}{calculerAge(lien.date_naissance) !== null && ` (${calculerAge(lien.date_naissance)} ans)`}</td>
                              <td style={{textAlign: 'center'}} onClick={e => e.stopPropagation()}>
                                <div style={{display: 'flex', gap: 4, justifyContent: 'center'}}>
                                  <button className="win-btn-small" onClick={() => handleDefinirCommeLienExclusif(lien, 1)} title="Définir comme Lien 1" style={{background: isLien1 ? '#0033A0' : '#e5e7eb', color: isLien1 ? 'white' : '#333', fontWeight: 'bold', padding: '4px 10px', fontSize: '12px', borderRadius: '4px', minWidth: '28px'}}>1</button>
                                  <button className="win-btn-small" onClick={() => handleDefinirCommeLienExclusif(lien, 2)} title="Définir comme Lien 2" style={{background: isLien2 ? '#0033A0' : '#e5e7eb', color: isLien2 ? 'white' : '#333', fontWeight: 'bold', padding: '4px 10px', fontSize: '12px', borderRadius: '4px', minWidth: '28px'}}>2</button>
                                </div>
                              </td>
                              <td style={{textAlign: 'center'}} onClick={e => e.stopPropagation()}>
                                <button className="win-btn-small win-btn-danger" onClick={() => handleSupprimerLienServeur(lien.id)} title="Supprimer">&#128465;</button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LiensTab;
